import { KlassProductInfo } from "types/klass";
import Blur from "page/user/Blur";
import { useRecoilValue } from "recoil";
import { tokenState } from "state";
import { SwiperSlide } from "swiper/react";
import SwiperKlass from "./SwiperKlass";
import { useProducts } from "api/market/product/hooks";
import { PuffLoader } from "react-spinners";
import ProductUnit from "@/component/market/product/ProductUnit";

const KlassRelatedProductSwiper = ({ lectureProductId }: { lectureProductId?: string }) => {
  const token = useRecoilValue(tokenState);
  const isAuthorizedUser = token && token.user_type;

  // MARK: 강의 연관 상품 ID 조회
  const { data: relatedProductIds, isLoading: isLoadingRelatedProductIds } = useProducts(
    {
      ids: [lectureProductId],
      showAll: true,
    },
    {
      enabled: !!lectureProductId,
      select: (response: KlassProductInfo) => {
        return response?.hits?.[0]?.related_product_ids || [];
      },
    },
  ) as { data: string[]; isLoading: boolean };

  // MARK: 강의 연관 상품 상세 정보 조회
  const { data: relatedProductInfoList, isLoading: isLoadingRelatedProductInfoList } = useProducts(
    {
      ids: relatedProductIds || [],
    },
    {
      enabled: !!relatedProductIds?.length,
      select: (response: KlassProductInfo) => {
        const hits = (response?.hits || []) as unknown as { id: string; [key: string]: any }[];
        // MARK: 원본 순서(related_product_ids)대로 정렬
        if (!relatedProductIds?.length) return hits;
        return relatedProductIds
          .map((id: string) => hits.find((hit) => hit.id === id))
          .filter(Boolean);
      },
    },
  ) as { data: KlassProductInfo["hits"]; isLoading: boolean };

  if (isLoadingRelatedProductIds || isLoadingRelatedProductInfoList)
    return (
      <div className="flex h-[200px] items-center justify-center">
        <PuffLoader size={30} color="#94969d" />
      </div>
    );
  if (!relatedProductInfoList?.length) return null;

  return (
    <>
      <h3 className="text-secondary-900 font-bold text-[20px] lg:text-[16px] leading-[30px] lg:leading-[24px] tracking-[-0.12px] lg:tracking-[-0.1px]">
        이 강의와 연관된 상품
      </h3>
      <div className="mt-[16px] lg:mt-[8px] relative">
        <SwiperKlass
          options={{
            breakpoints: {
              0: {
                slidesPerView: 3.55,
                spaceBetween: 8,
              },
            },
          }}
          navigationTop="top-[55px]"
        >
          {relatedProductInfoList?.map((product: { [key: string]: any }) => {
            return (
              <SwiperSlide key={product.id}>
                <div className="cursor-pointer group">
                  <ProductUnit
                    key={product.id}
                    id={product.id}
                    name={
                      product?.meta_title || product?.product?.meta_title || product?.name || ""
                    }
                    regularPrice={product.regular_price}
                    salePrice={product.sale_price}
                    thumbnail={product?.images?.[0]?.url || product?.image}
                    category_id={product?.category_id}
                    categoryIds={product?.category_ids || []}
                    productCode={product?.product_code}
                    brandName={product?.brand?.englishName}
                    isSoldOut={product?.is_sold_out || product?.discontinued}
                    isLoadingUnit={false}
                    product_group_ids={product?.product_group_ids}
                    cash_discounted_price_info={product?.cash_discounted_price_info}
                    isShowCashBadge={true}
                    cardSize="small"
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </SwiperKlass>

        {!isAuthorizedUser && (
          <div className="absolute bottom-[-5px] w-full h-[40px] left-0 backdrop-blur-sm z-10 flex items-center justify-center">
            <Blur />
          </div>
        )}
      </div>
    </>
  );
};

export default KlassRelatedProductSwiper;
